/**
 * Table of Contents-specific database operations
 */

import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";

/**
 * Save table of contents from offline data
 * @param {EduQuizDB} db - Database instance
 * @param {Array} resources - Resources array
 */
export async function saveTocs(db, resources) {
    const tocsData = [];
    const themesData = [];
    const partsData = [];
    const chaptersData = [];
    const chapterResourcesData = [];

    for (const resource of resources) {
        if (resource.type !== RESOURCE_TYPES.TABLE_OF_CONTENTS) continue;

        tocsData.push({
            serverResourceId: resource.id,
            name: resource.name,
            description: resource.description || null,
            level: resource.level || null,
            subject: resource.subject || null,
        });

        for (const theme of resource.themes || []) {
            themesData.push({
                serverId: theme.id,
                tocServerResourceId: resource.id,
                name: theme.name,
                description: theme.description || null,
                order: theme.order,
            });

            for (const part of theme.parts || []) {
                partsData.push({
                    serverId: part.id,
                    themeServerId: theme.id,
                    name: part.name,
                    description: part.description || null,
                    order: part.order,
                });

                for (const chapter of part.chapters || []) {
                    chaptersData.push({
                        serverId: chapter.id,
                        partServerId: part.id,
                        name: chapter.name,
                        description: chapter.description || null,
                        order: chapter.order,
                    });

                    for (const cr of chapter.resources || []) {
                        chapterResourcesData.push({
                            chapterServerId: chapter.id,
                            resourceServerId: cr.id,
                            resourceType: cr.resource_type,
                            order: cr.order,
                        });
                    }
                }
            }
        }
    }

    if (tocsData.length > 0) {
        await db.tableOfContents.bulkAdd(tocsData);
    }
    if (themesData.length > 0) {
        await db.tocThemes.bulkAdd(themesData);
    }
    if (partsData.length > 0) {
        await db.tocParts.bulkAdd(partsData);
    }
    if (chaptersData.length > 0) {
        await db.tocChapters.bulkAdd(chaptersData);
    }
    if (chapterResourcesData.length > 0) {
        await db.tocChapterResources.bulkAdd(chapterResourcesData);
    }
}

/**
 * Get a table of contents by server resource ID
 * @param {EduQuizDB} db - Database instance
 * @param {number} serverResourceId - Server resource ID
 */
export async function getToc(db, serverResourceId) {
    const toc = await db.tableOfContents
        .where("serverResourceId")
        .equals(serverResourceId)
        .first();

    if (!toc) return null;

    const themes = await db.tocThemes
        .where("tocServerResourceId")
        .equals(serverResourceId)
        .sortBy("order");

    toc.themes = await Promise.all(
        themes.map(async (theme) => {
            const parts = await db.tocParts
                .where("themeServerId")
                .equals(theme.serverId)
                .sortBy("order");

            theme.parts = await Promise.all(
                parts.map(async (part) => {
                    const chapters = await db.tocChapters
                        .where("partServerId")
                        .equals(part.serverId)
                        .sortBy("order");

                    part.chapters = await Promise.all(
                        chapters.map(async (chapter) => {
                            const chapterResources = await db.tocChapterResources
                                .where("chapterServerId")
                                .equals(chapter.serverId)
                                .sortBy("order");

                            chapter.resources = chapterResources;
                            return chapter;
                        })
                    );

                    return part;
                })
            );

            return theme;
        })
    );

    return toc;
}
